import {DataTable, DataTableProps} from "../DataTable/DataTable";
import {PipelineFlowEntity, Quantity} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {DefaultText} from "../../text/DefaultText/DefaultText";
import {useCallback} from "react";
import {ActionIcon, Group} from "@mantine/core";
import {FormattedQuantity} from "../../text/FormattedQuantity/FormattedQuantity";
import {FormattedDateTime} from "../../date/FormattedDateTime/FormattedDateTime";
import {HasAccess} from "../../auth/HasAccess/HasAccess"
import {IconEdit, IconTrash} from "@tabler/icons-react";
import {Badge} from "../../badge/Badge/Badge";
import {EntityAnchor} from "../../anchor/EntityAnchor/EntityAnchor";
import {usePagination} from "../DataTable/usePagination";
import {usePipelineFlowDataTableFilter} from "./usePipelineFlowDataTableFilter";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {DataTableSumFooter} from "../DataTable/footer/DataTableSumFooter/DataTableSumFooter";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";

const EMPTY_ARRAY: PipelineFlowEntity[] = []

export type PipelineFlowDataTableProps =
  EntityDataTableProps<PipelineFlowEntity>
  & {
  onEdit?: (record: PipelineFlowEntity) => void
  onDelete?: (record: PipelineFlowEntity) => void
}

export const PipelineFlowDataTable = (props: PipelineFlowDataTableProps) => {
  const {
    records: pipelineFlows = EMPTY_ARRAY,
    recordsPerPageOptions,
    onEdit,
    onDelete,
    ...rest
  } = props;

  const {t} = useTranslation(["common"]);

  const {filteredRecords, filterConfig} = usePipelineFlowDataTableFilter({
    records: pipelineFlows
  })

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'dttm', direction: 'asc'}
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const onEditClick = useCallback((record: PipelineFlowEntity) => {
    if (onEdit) {
      onEdit(record)
    }
  }, [onEdit])

  const onDeleteClick = useCallback((record: PipelineFlowEntity) => {
    if (onDelete) {
      onDelete(record)
    }
  }, [onDelete])

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={[
        {
          title: t("table.entity.pipelineflow.column.date", "Date"),
          accessor: "dttm",
          width: 100,
          sortable: true,
          render: ({dttm}) => {
            return <FormattedDateTime date={dttm} displayTime={false}/>
          },
        },
        {
          title: t("table.entity.pipelineflow.column.timegroup", "Time Group"),
          accessor: "timeGroup",
          width: 100,
          sortable: true,
          render: ({timeGroup}) => (
            t(timeGroup.displayValue.id, timeGroup.displayValue.defaultMessage)
          ),
        },
        {
          ...getColumnFilterProps(filterConfig, "object"),
          title: t("table.entity.pipelineflow.column.source", "Source"),
          accessor: "objectName",
          width: 100,
          sortable: true,
          render: ({objectId, objectType, objectName}) => (
            <EntityAnchor
              value={{
                objectId: objectId,
                objectName: objectName,
                objectType: objectType
              }}
            />
          ),
        },
        {
          title: t("table.entity.pipelineflow.column.quantity", "Qty"),
          accessor: "quantity",
          width: 100,
          sortable: true,
          render: ({quantity, unitOfMeasure}) => {
            const qty: Partial<Quantity> = {
              number: quantity,
              units: unitOfMeasure
            }

            return <FormattedQuantity value={qty}/>
          },
          footer: (
            <DataTableSumFooter
              value={{
                number: filteredRecords.reduce((prev, curr) => prev + curr.quantity, 0)
              }}
            />
          ),
        },
        {
          title: t("table.entity.pipelineflow.column.freighttype", "Freight Type"),
          accessor: "freightTypeName",
          width: 100,
          sortable: true,
          render: ({freightTypeName}) => (
            <DefaultText value={freightTypeName}/>
          )
        },
        {
          title: '',
          accessor: "actions",
          width: 100,
          textAlignment: 'right',
          render: (pipelineFlow) => (
            <Group spacing={4} position="right" noWrap>
              {pipelineFlow.meterGenerated &&
                <Badge>
                  {t("common.shard.metergenerated", "Meter Generated")}
                </Badge>
              }
              {!pipelineFlow.virtual &&
                <HasAccess permissions={["edit"]}>
                  <ActionIcon onClick={() => onEditClick(pipelineFlow)}>
                    <IconEdit size={16}/>
                  </ActionIcon>
                  <ActionIcon color="red" onClick={() => onDeleteClick(pipelineFlow)}>
                    <IconTrash size={16}/>
                  </ActionIcon>
                </HasAccess>
              }
            </Group>
          )
        }
      ]}
    />
  );
};
